import { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import defaultTheme from '../themes/templates/default.json';
import { authService } from '../services/authService';
import usersService from '../services/usersService';
import { themeTypeToId } from '../constants/ThemeType';
import ThemeSelectionModal from '../components/ThemeSelectionModal';

// Context para o tema
export const ThemeCustomContext = createContext();

/**
 * Carrega um tema pelo ID
 * @param {string} themeId - ID do tema a ser carregado
 * @returns {Promise<Object>} Configuração do tema
 */
async function loadTheme(themeId) {
  try {
    // Carrega dinamicamente o arquivo JSON do tema
    const theme = await import(`../themes/templates/${themeId}.json`);
    return theme.default || theme;
  } catch (error) {
    console.warn(`Tema "${themeId}" não encontrado. Usando tema padrão.`, error);
    return defaultTheme;
  }
}

/**
 * Mescla o tema customizado com o tema padrão
 * Garante que todas as propriedades existam, usando o padrão como fallback
 * @param {Object} customTheme - Tema customizado
 * @returns {Object} Tema mesclado
 */
function mergeWithDefault(customTheme) {
  return {
    id: customTheme.id || defaultTheme.id,
    name: customTheme.name || defaultTheme.name,
    description: customTheme.description || defaultTheme.description,
    colors: { ...defaultTheme.colors, ...(customTheme.colors || {}) },
    fonts: { ...defaultTheme.fonts, ...(customTheme.fonts || {}) },
    icons: { ...defaultTheme.icons, ...(customTheme.icons || {}) },
    labels: { ...defaultTheme.labels, ...(customTheme.labels || {}) },
    messages: { ...defaultTheme.messages, ...(customTheme.messages || {}) },
    serviceCategories: customTheme.serviceCategories || defaultTheme.serviceCategories,
    customStyles: customTheme.customStyles || defaultTheme.customStyles || {},
  };
}

/**
 * Provider do contexto de temas customizados
 * Gerencia o tema ativo e fornece funções para mudança
 */
export function ThemeCustomProvider({ children, disableThemeSelection = false }) {
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const [themeId, setThemeId] = useState('default');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [showThemeSelection, setShowThemeSelection] = useState(false);

  // Lista de temas disponíveis (pode ser expandida dinamicamente)
  const availableThemes = useMemo(() => [
    { id: 'default', name: 'Padrão' },
    { id: 'barbershop', name: 'Barbearia' },
    { id: 'clinic', name: 'Clínica Médica' },
    { id: 'tattoo-studio', name: 'Studio de Tatuagem' },
    { id: 'beauty-salon', name: 'Salão Feminino' },
    { id: 'psychology', name: 'Psicologia' },
    { id: 'aesthetics', name: 'Estética' },
    { id: 'massage', name: 'Massoterapia' },
    { id: 'dental', name: 'Odontologia' },
    { id: 'medical', name: 'Clínica Médica Geral' },
    { id: 'therapy', name: 'Terapias Holísticas' },
    { id: 'teacher', name: 'Professor' },
    { id: 'personal-trainer', name: 'Personal Trainer' },
  ], []);

  /**
   * Carrega um tema pelo ID
   */
  const changeTheme = async (newThemeId) => {
    setLoading(true);
    try {
      const loadedTheme = await loadTheme(newThemeId);
      const mergedTheme = mergeWithDefault(loadedTheme);
      setCurrentTheme(mergedTheme);
      setThemeId(newThemeId);
      
      // Salva preferência no localStorage
      localStorage.setItem('agendario_theme', newThemeId);
      
      // Aplica CSS variables
      applyCssVariables(mergedTheme);
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Aplica as cores do tema como CSS variables no :root
   */
  const applyCssVariables = (theme) => {
    const root = document.documentElement;
    
    // Aplica cores
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-color-${key}`, value);
    });

    // Aplica fontes
    Object.entries(theme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--theme-font-${key}`, value);
    });
  };

  /**
   * Obtém um valor do tema com fallback para o padrão
   */
  const getThemeValue = (path) => {
    const keys = path.split('.');
    let value = currentTheme;
    let defaultValue = defaultTheme;

    for (const key of keys) {
      value = value?.[key];
      defaultValue = defaultValue?.[key];
      
      if (value === undefined) {
        return defaultValue;
      }
    }

    return value !== undefined ? value : defaultValue;
  };

  /**
   * Obtém um ícone do tema
   */
  const getIcon = (iconKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.icons?.[iconKey] || defaultTheme.icons?.[iconKey] || { name: 'HelpOutline' };
    console.debug(`[ThemeCustomContext.getIcon] "${iconKey}" →`, {
      found: !!theme.icons?.[iconKey],
      result,
      currentThemeId: themeId,
      hasCurrentTheme: !!currentTheme,
    });
    return result;
  };

  /**
   * Obtém um label do tema
   */
  const getLabel = (labelKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.labels?.[labelKey] || defaultTheme.labels?.[labelKey] || labelKey;
    console.debug(`[ThemeCustomContext.getLabel] "${labelKey}" →`, {
      found: !!theme.labels?.[labelKey],
      result,
      currentThemeId: themeId,
      hasCurrentTheme: !!currentTheme,
    });
    return result;
  };

  /**
   * Obtém uma mensagem do tema
   */
  const getMessage = (messageKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.messages?.[messageKey] || defaultTheme.messages?.[messageKey] || messageKey;
    console.debug(`[ThemeCustomContext.getMessage] "${messageKey}" →`, { result });
    return result;
  };

  /**
   * Obtém uma cor do tema
   */
  const getColor = (colorKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.colors?.[colorKey] || defaultTheme.colors?.[colorKey] || '#000000';
    console.debug(`[ThemeCustomContext.getColor] "${colorKey}" →`, { result });
    return result;
  };

  // Carrega o tema salvo no localStorage ao montar
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // 0. IMPORTANTE: Processar token do query string ANTES de tudo
        authService.processLoginCallback();

        // 1. Verificar localStorage primeiro - tema persistido
        const savedThemeId = localStorage.getItem('agendario_theme');
        
        if (savedThemeId && savedThemeId !== 'default') {
          console.log('[THEME] Tema encontrado no localStorage (agendario_theme):', savedThemeId);
          await changeTheme(savedThemeId);
          setInitializing(false);
          return;
        }

        // 2. Verificar JWT
        const themeFromJwt = authService.getThemeFromToken();
        
        if (themeFromJwt) {
          console.log('[THEME] Tema encontrado no JWT:', themeFromJwt);
          const themeIdFromJwt = themeTypeToId(themeFromJwt);
          await changeTheme(themeIdFromJwt);
          setInitializing(false);
          return;
        }

        // 3. Nenhum tema encontrado - verificar se token é válido antes de exibir modal
        console.log('[THEME] Nenhum tema encontrado.');
        
        // Se estivermos em modo onboarding, não exibir modal
        if (disableThemeSelection) {
          console.log('[THEME] Modo onboarding detectado. Não exibindo modal de seleção.');
          applyCssVariables(defaultTheme);
          setInitializing(false);
          return;
        }

        // Se o onboarding já foi completado, usar tema padrão (nunca exibir modal)
        const sessionData = localStorage.getItem('agendario_onboarding_session');
        const onboardingCompleted = sessionData ? JSON.parse(sessionData).step === 'completed' : false;
        if (onboardingCompleted) {
          console.log('[THEME] Onboarding já foi completado. Usando tema padrão.');
          applyCssVariables(defaultTheme);
          setInitializing(false);
          return;
        }
        
        // Validar token antes de exibir modal de seleção
        if (!authService.isTokenValid()) {
          console.log('[THEME] Token inválido ou expirado. Redirecionando para login.');
          authService.redirectToLogin();
          return;
        }
        
        console.log('[THEME] Token válido. Exibindo modal de seleção.');
        applyCssVariables(defaultTheme);
        setInitializing(false);
        setShowThemeSelection(true);
      } catch (error) {
        console.error('[THEME] Erro ao inicializar tema:', error);
        applyCssVariables(defaultTheme);
        setInitializing(false);
      }
    };

    initializeTheme();
  }, []);

  /**
   * Callback quando usuário seleciona tema pela primeira vez
   */
  const handleThemeSelected = async (theme) => {
    console.log('[THEME] Tema selecionado pelo usuário:', theme);
    
    // ORDEM CORRETA: Salvar no backend PRIMEIRO
    console.log('[THEME] Salvando tema no backend...');
    await usersService.updateTheme(theme.type);
    console.log('[THEME] Tema salvo no backend com sucesso');
    
    // Só depois de salvar com sucesso, aplicar localmente
    console.log('[THEME] Aplicando tema localmente...');
    await changeTheme(theme.id);
    
    // Fechar modal
    setShowThemeSelection(false);
    
    console.log('[THEME] Processo completo com sucesso');
  };

  const value = {
    currentTheme,
    themeId,
    loading,
    initializing,
    availableThemes,
    changeTheme,
    getThemeValue,
    getIcon,
    getLabel,
    getMessage,
    getColor,
  };

  // Exibe loading durante inicialização do tema
  if (initializing) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            Carregando tema...
          </p>
        </div>
      </div>
    );
  }

  // Exibe tela de seleção de tema (bloqueia completamente o sistema)
  if (showThemeSelection) {
    return <ThemeSelectionModal onThemeSelected={handleThemeSelected} />;
  }

  // Sistema carregado com tema definido
  return (
    <ThemeCustomContext.Provider value={value}>
      {children}
    </ThemeCustomContext.Provider>
  );
}

ThemeCustomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
